import type { NextRouter } from "next/router";
import {
  ROUTE_TITLES,
  getPageTitle,
} from "@src/features/app/top-nav/pageTitle";

type RouterLike = Pick<NextRouter, "pathname" | "query" | "asPath">;

// Item kinds the backend understands (`opus_agent` renders "Currently
// viewing" from them and resolves library_item/user/location/role/folder to
// names). Keyed by the Next.js route pattern; the id is the route's `[id]`.
const ROUTE_ITEM_KINDS: Record<string, string> = {
  "/library/library-item/[id]": "library_item",
  "/reporting/knowledge-check/[libraryItemId]": "library_item",
  "/library/folders/[id]": "folder",
  "/users/[id]": "user",
  "/locations/[id]": "location",
  "/locations/brands/[id]": "brand",
  "/locations/franchises/[id]": "franchise",
  "/locations/regions/[id]": "region",
  "/roles/[id]": "role",
  "/roles/departments/[id]": "department",
  "/paths/[id]": "workflow",
  "/tasks/[id]": "task",
  "/workflows/[id]": "workflow",
  "/agents/[id]": "agent",
};

const TITLE_MAX_CHARS = 120;
const URL_MAX_CHARS = 300;
const ID_PATTERN =
  /^(?:\d+|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i;
const TAB_PATTERN = /^[\w-]{1,40}$/;
const SENSITIVE_PARAM =
  /token|code|key|secret|password|auth|session|signature/i;

const decodeEscapes = (value: string): string =>
  value.replace(/%([0-9a-f]{2})/gi, (_, hex: string) =>
    String.fromCharCode(parseInt(hex, 16)),
  );

const isSensitive = (pair: string): boolean => {
  const name = pair.split("=")[0].replace(/\+/g, " ");
  return (
    SENSITIVE_PARAM.test(name) || SENSITIVE_PARAM.test(decodeEscapes(name))
  );
};

const withoutSensitiveParams = (params: string): string =>
  params
    .split(/[&;]/)
    .filter((pair) => pair && !isSensitive(pair))
    .join("&");

// The page's full URL minus credential-like params (query and hash). ";" and
// whitespace are percent-encoded so the value cannot end its pair early.
const safeUrl = (asPath: string): string => {
  const [beforeHash, ...hashParts] = asPath.split("#");
  const hash = hashParts.join("#");
  const [path, ...queryParts] = beforeHash.split("?");
  const query = withoutSensitiveParams(queryParts.join("?"));
  const keptHash = withoutSensitiveParams(hash);
  const url = `${path}${query ? `?${query}` : ""}${keptHash ? `#${keptHash}` : ""}`;
  const flat = Array.from(url, (char) => {
    const code = char.charCodeAt(0);
    return code < 0x20 || code === 0x7f || code === 0x85 ? " " : char;
  }).join("");
  const encoded = flat.replace(/\s+/g, "%20").replace(/;/g, "%3B");
  return encoded.length > URL_MAX_CHARS
    ? encoded
        .slice(0, URL_MAX_CHARS)
        .replace(/[\ud800-\udbff]$/, "")
        .replace(/%[0-9a-f]?$/i, "")
    : encoded;
};

const routeId = (value: unknown): string | null =>
  typeof value === "string" && ID_PATTERN.test(value) ? value : null;

// One line for a value: no newlines, and no ";" unless the key is the last
// on the line (the separator), so the backend's split stays unambiguous.
const clean = (
  value: string,
  {
    max,
    allowSemicolon = false,
  }: { max?: number; allowSemicolon?: boolean } = {},
): string => {
  const flat = value.replace(/\s+/g, " ").trim();
  const safe = allowSemicolon ? flat : flat.replace(/;/g, ",");
  return max !== undefined && safe.length > max ? safe.slice(0, max) : safe;
};

// The top nav's title for the route. On /library without a type filter the
// nav says "All", which names a tab, not a place — the section name is the
// useful word there.
const sectionTitle = (pathname: string, query: RouterLike["query"]): string => {
  const title = getPageTitle(pathname, query);
  return title === "All" ? ROUTE_TITLES[pathname] ?? title : title;
};

const routeItem = ({ pathname, query }: RouterLike): string | null => {
  const kind = ROUTE_ITEM_KINDS[pathname];
  if (kind) {
    const param = pathname.slice(pathname.lastIndexOf("[") + 1, -1);
    const id = routeId(query[param]);
    return id ? `${kind}:${id}` : null;
  }
  // /library and /library/forms open a builder in the right pane when
  // libraryItemId is present (same rule as isBuilderView).
  const libraryItemId = routeId(query.libraryItemId);
  if (
    (pathname === "/library" || pathname === "/library/forms") &&
    libraryItemId
  ) {
    return `library_item:${libraryItemId}`;
  }
  return null;
};

// What the admin is looking at, sent as liveContext with every Opus Agent
// message. One line of "key=value" pairs joined by "; " in the order route,
// item, tab, url, title — the format agreed with the backend, which resolves
// the item to a name and renders the line as "Currently viewing". The title is
// the route-derived section title, never anything the user typed.
export const getLiveContext = (router: RouterLike): string => {
  const { pathname, query, asPath } = router;
  const route = clean(asPath.split(/[?#]/)[0] || pathname);
  const parts = [`route=${route}`];
  const item = routeItem(router);
  if (item) parts.push(`item=${item}`);
  if (typeof query.tab === "string" && TAB_PATTERN.test(query.tab)) {
    parts.push(`tab=${query.tab}`);
  }
  const url = safeUrl(asPath);
  if (url) parts.push(`url=${url}`);
  const title = clean(sectionTitle(pathname, query), {
    max: TITLE_MAX_CHARS,
    allowSemicolon: true,
  });
  if (title) parts.push(`title=${title}`);
  return parts.join("; ");
};
